import { useEffect, type RefObject } from 'react'

// Cerrar una capa al hacer clic FUERA de ella y con Escape (diseño v2 §3.8, punto 4
// de Damián: "Al abrir el chat y dar clic en cualquier lugar fuera de él, debería
// colapsarse de regreso").
//
// Las cuatro reglas que este hook respeta, y POR QUÉ cada una:
//
// 1. pointerdown en fase de CAPTURA. Con 'click' en burbujeo, un elemento que se
//    desmonta a sí mismo en su onClick (una fila de lista, un menú) ya no está en
//    el árbol cuando llega el evento al documento: el contains() da false y la capa
//    se cerraría por accidente. pointerdown en captura ve el objetivo intacto.
// 2. NO se llama preventDefault ni stopPropagation. El clic de fuera sigue su curso
//    y hace su acción normal (abrir un proyecto, marcar una casilla) ADEMÁS de
//    cerrar el panel. Ese fue el requisito textual: no robar el clic.
// 3. Las capas flotantes propias no cuentan como "fuera". Los menús, popovers y
//    modales que la capa abre se pintan en otro punto del árbol (o encima), así que
//    un contains() del panel no los reconoce. Se excluyen por selector: .pop,
//    .modal, .modal-overlay y el atajo [data-capa-flotante], que es lo único que
//    tiene que llevar cualquier capa flotante nueva.
// 4. Escape NO se captura: se escucha en burbujeo, para que un menú o un modal
//    encima pueda quedárselo con stopPropagation y cerrarse él primero. El panel
//    solo se cierra cuando ya no queda nada arriba (Escape en cascada).
//    RED DE SEGURIDAD (por qué existe): confiar SOLO en el stopPropagation de la
//    capa de encima es frágil — basta que una capa nueva llame preventDefault y se
//    olvide del stopPropagation para que un Escape cierre las dos de golpe y se
//    lleve por delante el borrador del composer. Por eso aquí se ignora también el
//    Escape que ya viene con defaultPrevented (alguien arriba ya lo atendió) y el
//    que nace DENTRO de una capa flotante propia (el mismo selector del punto 3).
//
// El disparador que ABRE la capa (el hexágono del chat, un botón de menú) se pasa en
// `ignorar`: sin esa excepción, un clic en él cerraría por este listener y volvería a
// abrir por su propio toggle, y el panel nunca se cerraría desde su botón.

/** Selector de las capas flotantes que NUNCA cuentan como "fuera". */
export const SELECTOR_CAPAS_FLOTANTES = '.pop, .modal, .modal-overlay, [data-capa-flotante]'

/**
 * ¿El objetivo de un evento está FUERA de todos los elementos dados?
 * Puro y sin React: es lo que se prueba en useCerrarAlClicFuera.test.ts.
 */
export function esFuera(destino: EventTarget | null, elementos: (Element | null | undefined)[]): boolean {
  if (!(destino instanceof Node)) return true
  for (const el of elementos) {
    if (el && el.contains(destino)) return false
  }
  // Element.closest solo existe en elementos; un click sobre un nodo de texto llega
  // como Element igual (el objetivo de un evento de puntero nunca es un texto), pero
  // el guardia evita reventar si alguien dispara el evento a mano sobre el document.
  const comoElemento = destino instanceof Element ? destino : destino.parentElement
  if (comoElemento?.closest(SELECTOR_CAPAS_FLOTANTES)) return false
  return true
}

/**
 * ¿Este Escape ya lo atendió alguien de más arriba? Puro, para poder probarlo.
 * Dos señales: `defaultPrevented` (la capa de encima lo consumió) y un objetivo que
 * vive dentro de una capa flotante propia (un menú, un popover, un modal abierto
 * SOBRE la capa: ese Escape es suyo, no del panel de abajo).
 */
export function escapeYaAtendido(e: KeyboardEvent): boolean {
  if (e.defaultPrevented) return true
  const destino = e.target
  const comoElemento =
    destino instanceof Element ? destino : destino instanceof Node ? destino.parentElement : null
  return !!comoElemento?.closest(SELECTOR_CAPAS_FLOTANTES)
}

/**
 * ¿Hay AHORA MISMO alguna capa flotante propia montada en el documento?
 *
 * POR QUÉ no basta `escapeYaAtendido` para la cascada: las dos capas escuchan el
 * keydown en el MISMO nodo (document), así que ni stopPropagation ni
 * preventDefault ordenan nada entre ellas — mandan el orden de registro, y la
 * capa de abajo se monta ANTES, o sea que su listener corre primero y ya cerró
 * cuando la de arriba llama preventDefault. Con el foco en el <body> tampoco
 * sirve mirar el objetivo del evento. Preguntarle al DOM quién está abierto es
 * lo único que no depende de ese orden.
 *
 * `raiz` acota la pregunta cuando la capa que consulta ya vive dentro de otra.
 */
export function hayCapaFlotanteAbierta(raiz: ParentNode = document): boolean {
  return !!raiz.querySelector(SELECTOR_CAPAS_FLOTANTES)
}

/**
 * Monta los listeners de "clic fuera" y "Escape" mientras el componente esté vivo.
 * Móntalo SOLO dentro del componente que existe con la capa abierta: así no queda
 * ni un listener con la capa cerrada (disciplina de realtime-y-reglas.md).
 */
export function useCerrarAlClicFuera(
  ref: RefObject<HTMLElement | null>,
  cerrar: () => void,
  ignorar: RefObject<HTMLElement | null>[] = [],
) {
  useEffect(() => {
    const alBajar = (e: PointerEvent) => {
      const fuera = esFuera(e.target, [ref.current, ...ignorar.map((r) => r.current)])
      if (fuera) cerrar()
    }
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (escapeYaAtendido(e)) return
      cerrar()
    }
    document.addEventListener('pointerdown', alBajar, true)
    document.addEventListener('keydown', alTeclear)
    return () => {
      document.removeEventListener('pointerdown', alBajar, true)
      document.removeEventListener('keydown', alTeclear)
    }
    // `ignorar` es un arreglo nuevo en cada render si el call site lo escribe inline:
    // se depende de su LONGITUD, no de la identidad, porque los refs son estables.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, cerrar, ignorar.length])
}
